import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("La password deve avere almeno 8 caratteri"); return; }
    if (pw !== pw2) { setError("Le password non coincidono"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reimpostata. Ora puoi accedere.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #0ea5e9 0%, transparent 45%), radial-gradient(circle at 80% 70%, #10b981 0%, transparent 40%)" }} />
        <div className="relative flex items-center gap-3">
          <div className="flex items-center justify-center rounded-xl bg-white px-4 py-3 shadow-sm">
            <img src="/sms-logo.jpg" alt="SMS SRL — Servizi Finanziari" className="h-20 w-auto" />
          </div>
        </div>
        <div className="relative space-y-5">
          <h1 className="font-heading text-4xl font-bold leading-tight">Nuova password</h1>
        </div>
        <p className="relative text-xs text-slate-500">© 2026 FinPractice Pro — Milano</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-8" data-testid="reset-password-page">
          <div>
            <h2 className="font-heading text-2xl font-bold">Reimposta la password</h2>
            <p className="text-sm text-muted-foreground mt-1">Scegli una nuova password per il tuo account.</p>
          </div>

          {!token ? (
            <div className="space-y-4">
              <p className="text-sm text-destructive" data-testid="reset-no-token">Link non valido: manca il codice di reset. Richiedi un nuovo link di recupero.</p>
              <Button variant="outline" className="w-full" onClick={() => navigate("/forgot-password")}>Richiedi nuovo link</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pw">Nuova password</Label>
                <Input id="pw" data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw2">Conferma password</Label>
                <Input id="pw2" data-testid="reset-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="••••••••" required />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
              <Button type="submit" data-testid="reset-submit-button" className="w-full" disabled={busy}>{busy ? "Salvataggio…" : "Reimposta password"}</Button>
              <button type="button" onClick={() => navigate("/login")} className="w-full text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5" data-testid="reset-back-login"><ArrowLeft className="h-4 w-4" />Torna al login</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
